import React from 'react';
import { Calendar } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { TimesheetEntry } from '@/types/timesheet';
import { TimesheetGridHeader, type TimesheetGridHeaderProps } from '../grid-header/TimesheetGridHeader';
import { TimesheetGridFooter, type TimesheetGridFooterProps } from '../grid-footer/TimesheetGridFooter';
import { TimesheetGridRow, type TimesheetGridRowProps } from '../row/TimesheetGridRow';

/** Row props that are identical for every row — the per-row ones are supplied here. */
export type TimesheetGridSharedRowProps = Omit<TimesheetGridRowProps, 'entry' | 'index' | 'rowTotal'>;

export interface TimesheetGridTableProps {
  entries: TimesheetEntry[];
  rowTotals: number[];
  /** Number of columns rendered in a row, used by the empty state. */
  gridColumnCount: number;
  tableScrollRef: React.RefObject<HTMLDivElement | null>;
  header: TimesheetGridHeaderProps;
  rows: TimesheetGridSharedRowProps;
  footer: TimesheetGridFooterProps;
}

/** Scrollable grid: sticky header, entry rows (or the empty state) and the totals footer. */
export function TimesheetGridTable({
  entries,
  rowTotals,
  gridColumnCount,
  tableScrollRef,
  header,
  rows,
  footer,
}: TimesheetGridTableProps) {
  return (
    <Card data-tour="timesheet-grid" className="max-h-full flex flex-col">
      <CardContent className="p-0 flex-1 flex flex-col min-h-0 overflow-hidden">
        <div ref={tableScrollRef} className="flex-1 overflow-x-auto overflow-y-auto min-h-0">
          <table className="w-full table-fixed border-collapse text-sm">
            <TimesheetGridHeader {...header} />
            <tbody>
              {entries.length === 0 && (
                <tr>
                  <td colSpan={gridColumnCount} className="text-center align-middle py-12">
                    <div className="flex flex-col items-center gap-4">
                      <div className="rounded-full bg-muted p-4">
                        <Calendar className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-foreground">No timesheet entries yet</p>
                        <p className="text-xs text-muted-foreground">Add your first row to start logging your time for this week</p>
                      </div>

                    </div>
                  </td>
                </tr>
              )}
              {entries.map((entry, index) => (
                <TimesheetGridRow
                  key={entry.id}
                  entry={entry}
                  index={index}
                  rowTotal={rowTotals[index]}
                  {...rows}
                />
              ))}
            </tbody>
            <TimesheetGridFooter {...footer} />
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
